import isInteger from 'lodash-es/isInteger'

import {
  DIGITAL_PRODUCTS_FEE_PERCENTAGE,
  SINGLE_UNIT_PRODUCT_ORDER_SHIPPING_FEES,
  SINGLE_UNIT_PRODUCT_ORDER_TAXES_FEES,
  SINGLE_UNIT_PRODUCT_PAYMENT_PROCESSING_FEE_PERCENT,
  SINGLE_UNIT_PRODUCT_PAYMENT_PROCESSING_FEE_VALUE,
} from '@fourthwall/utils/dist/constants'

import type { OfferType } from '../../../SingleProduct.types'
import { MAX_SOLD_QUANTITY_VALUE } from './consts'
import type { QuantityPricing, SizesPerCostType } from './types'

export const getSuggestions = (offerType: OfferType, sizePriceSuggestion?: string): string[] => {
  const suggestions: string[] = []

  if (offerType === 'ON_DEMAND') {
    suggestions.push(
      'On-demand products have a single manufacturing price, regardless of how many are ordered.',
    )
  }

  if (offerType === 'MOQ') {
    suggestions.push(
      'Profit for screen printed products is determined by the number of items sold. The more you sell, the more you make per item.',
    )
  }

  if (sizePriceSuggestion) {
    suggestions.push(sizePriceSuggestion)
  }

  return suggestions
}

export const getEstimatedPaymentProcessingFees = (price: number | string) => {
  const feeValue =
    (Number(price) +
      SINGLE_UNIT_PRODUCT_ORDER_TAXES_FEES +
      SINGLE_UNIT_PRODUCT_ORDER_SHIPPING_FEES) *
      (SINGLE_UNIT_PRODUCT_PAYMENT_PROCESSING_FEE_PERCENT / 100) +
    SINGLE_UNIT_PRODUCT_PAYMENT_PROCESSING_FEE_VALUE

  return Number(feeValue.toFixed(2))
}

export const getUnitProfit = (price: number, cost?: number | null, includeFees?: boolean) => {
  const profit = price - (cost || 0) - (includeFees ? getEstimatedPaymentProcessingFees(price) : 0)

  return Number(profit.toFixed(2))
}

export const getTotalProfit = (unitProfit: number, quantity: number) =>
  Number((unitProfit * quantity).toFixed(2))

export const getAutoAdjustedSellingPrice = (profit: number, cost?: number | null) =>
  profit + (cost || 0)

export const getSizeTabLabel = (sizes: string[]) =>
  sizes.length > 1 ? `${sizes[0]} to ${sizes[sizes.length - 1]}` : sizes[0]

export const getMOQPricingData = (
  firstItemOnly: boolean,
  includeFees: boolean,
  priceValue: number,
  sizesPerCost: SizesPerCostType,
  pricing?: QuantityPricing[],
) => {
  const data = firstItemOnly ? [sizesPerCost[0]] : sizesPerCost

  return (
    data.map(({ sizes }) => {
      const label = sizes ? getSizeTabLabel(sizes) : ''
      const quantityPricing = pricing?.map((quantityPricingItem) => {
        const cost = quantityPricingItem.perSize.filter(
          (perSizeItem) => perSizeItem.size === sizes[0],
        )[0]?.cost.value
        const price = getAutoAdjustedSellingPrice(
          getUnitProfit(priceValue, quantityPricingItem.perSize[0]?.cost.value || 0),
          cost,
        )
        const unitProfit = getUnitProfit(price, cost, includeFees)
        const totalProfit = getTotalProfit(unitProfit, quantityPricingItem.numberOfUnits)
        const feesValue = getEstimatedPaymentProcessingFees(price)

        return {
          numberOfUnits: quantityPricingItem.numberOfUnits,
          price,
          cost,
          unitProfit,
          totalProfit,
          feesValue,
        }
      })

      return { label, quantityPricing }
    }) || []
  )
}

export const getOnDemandPricingData = (
  firstItemOnly: boolean,
  includeFees: boolean,
  priceValue: number,
  sizesPerCost: SizesPerCostType,
) => {
  const data = firstItemOnly ? [sizesPerCost[0]] : sizesPerCost

  return (
    data.map(({ cost, sizes }, index) => {
      const label = getSizeTabLabel(sizes)
      const price =
        index === 0
          ? priceValue
          : getAutoAdjustedSellingPrice(getUnitProfit(priceValue, sizesPerCost[0].cost), cost)
      const unitProfit = getUnitProfit(price, cost, includeFees)
      const feesValue = getEstimatedPaymentProcessingFees(price)

      return { cost, label, price, unitProfit, feesValue }
    }) || []
  )
}

export const getDigitalCost = (
  price: number,
  digitalFeePercentage = DIGITAL_PRODUCTS_FEE_PERCENTAGE,
) => price * (digitalFeePercentage / 100)

export const isSoldValueValid = (value: number, minimumOrderQuantity: number) =>
  value >= minimumOrderQuantity && value < MAX_SOLD_QUANTITY_VALUE && isInteger(Number(value))

export const getSoldValueError = (value: string, minimumOrderQuantity: number) => {
  if (value.length && !isInteger(Number(value))) {
    return 'The value is wrong.'
  }

  if (!!minimumOrderQuantity && parseInt(value) < minimumOrderQuantity) {
    return `Minimum ${minimumOrderQuantity} required.`
  }

  if (parseInt(value) > MAX_SOLD_QUANTITY_VALUE) {
    return `The value is too large.`
  }
}
